import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth, type User } from "./AuthContext";
import { isSuperAdminIdentity } from "../utils/jwtIdentity";
import { fetchMenuAccess } from "../utils/orgStore";
import {
  ModuleAccess,
  ModuleAction,
  PERMISSIONS_CHANGED_EVENT,
  actionForRequest,
  moduleForPath,
  normalizeMenuName,
  setRequestGuard,
} from "../utils/modulePermissions";

/** How often an open session re-reads its role's permissions, so an admin's
 * change reaches users who are already logged in. */
const REFRESH_INTERVAL_MS = 60_000;
/** Focus and tab switches refresh too, but not more often than this. */
const MIN_REFRESH_GAP_MS = 10_000;

interface PermissionsContextType {
  /** True until the first permission read for this user finishes. */
  loading: boolean;
  /** False only when this user's role is gated and lacks the permission. */
  can: (menuName: string, action: ModuleAction) => boolean;
  /** Re-read the current role's permissions now. */
  refresh: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

/** What the logged-in role may do on the page currently open — for hiding
 * Add / Edit / Delete controls. Pages outside the route map (and ungated
 * users) get full access; the request guard in apiFetch backs this up. */
export const usePageAccess = () => {
  const { pathname } = useLocation();
  const { can } = usePermissions();
  const module = moduleForPath(pathname);
  const canEdit = !module || can(module.menuName, "edit");
  const canDelete = !module || can(module.menuName, "delete");
  return { canEdit, canDelete, readOnly: !canEdit };
};

/** Does this identity's role get gated at all? A SuperAdmin's own role isn't
 * scoped by a tenant's menu grid, and the Control Center's "View Tenant"
 * preview identity is fabricated with no roleId — both stay unrestricted. */
const needsGate = (
  user: User | null
): user is User & { accountId: string; roleId: string } =>
  !!user &&
  !isSuperAdminIdentity(user) &&
  !!user.roleId &&
  !!Number(user.accountId) &&
  !!Number(user.roleId);

const ACTION_LABEL: Record<ModuleAction, string> = {
  view: "view",
  edit: "add or change",
  delete: "delete",
};

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  // null = unrestricted. Also the fallback when the very first read fails —
  // a transient API error shouldn't strand a legitimate user with no
  // navigation at all. A failed REFRESH keeps the last good answer instead.
  const [access, setAccess] = useState<Map<string, ModuleAccess> | null>(null);
  const [loading, setLoading] = useState(() => needsGate(user));
  const lastFetchRef = useRef(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const accountId = needsGate(user) ? Number(user.accountId) : 0;
  const roleId = needsGate(user) ? Number(user.roleId) : 0;
  const userId = needsGate(user) ? Number(user.id) : 0;

  // First read whenever the logged-in identity changes.
  useEffect(() => {
    if (!accountId || !roleId) {
      setAccess(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    lastFetchRef.current = Date.now();
    fetchMenuAccess(accountId, roleId, userId)
      .then((next) => {
        if (!cancelled) setAccess(next);
      })
      .catch(() => {
        if (!cancelled) setAccess(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, roleId, userId]);

  // Later reads: silent, and a failure keeps what we already have.
  useEffect(() => {
    if (refreshTick === 0 || !accountId || !roleId) return;
    let cancelled = false;
    lastFetchRef.current = Date.now();
    fetchMenuAccess(accountId, roleId, userId)
      .then((next) => {
        if (!cancelled) setAccess(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [refreshTick, accountId, roleId, userId]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (!accountId || !roleId) return;
    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFetchRef.current < MIN_REFRESH_GAP_MS) return;
      refresh();
    };
    const timer = window.setInterval(refreshIfStale, REFRESH_INTERVAL_MS);
    window.addEventListener("focus", refreshIfStale);
    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshIfStale);
      document.removeEventListener("visibilitychange", refreshIfStale);
      window.removeEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
    };
  }, [accountId, roleId, refresh]);

  const can = useCallback(
    (menuName: string, action: ModuleAction) => {
      if (!access) return true;
      const entry = access.get(normalizeMenuName(menuName));
      return !!entry && entry[action];
    },
    [access]
  );

  // Every API write made from an /admin page is checked against that page's
  // module, so a View-only role can't save or delete from it. Calls from
  // outside /admin (login, guest app) aren't gated.
  useEffect(() => {
    if (!access) {
      setRequestGuard(null);
      return;
    }
    setRequestGuard((method, path) => {
      const action = actionForRequest(method, path);
      if (!action) return null;
      const module = moduleForPath(window.location.pathname);
      if (!module || can(module.menuName, action)) return null;
      return `Your role doesn't allow you to ${ACTION_LABEL[action]} records here. Ask an administrator for access.`;
    });
    return () => setRequestGuard(null);
  }, [access, can]);

  return (
    <PermissionsContext.Provider value={{ loading, can, refresh }}>
      {children}
    </PermissionsContext.Provider>
  );
};
